import alt from 'components/Dispatcher';
import moment from 'moment';
import WebDataRocksActions, {
  WebDataRocksActionsInterface,
  WebDataType,
} from './WebDataRocksActions';
import WebDataRocks, {Report} from 'webdatarocks';
import pickBy from 'lodash/pickBy';
import sortBy from 'lodash/sortBy';
import {Store, StoreMethods} from '@store/@types/alt';
import {WidgetConfig, WidgetType} from '@store/@types/dash';
import {localization} from './WebDataRocksLocalization';
import byteSize from 'utils/byteSize';
import {OrderedSet} from 'immutable';
import Flexmonster from 'flexmonster';
import {LocalStorageKey} from '@store/@types/util/LocalStorageKey';

type Pivot = WebDataRocks.Pivot | Flexmonster.Pivot;
export interface WebDataRocksStoreState {
  readonly hidden: boolean;
  readonly data: any[];
  readonly wdr: Pivot | null;
  readonly title: string | null;
  readonly dataType?: WebDataType;
  readonly settingsLoaded: boolean;
  readonly dataLoaded: boolean;
  readonly suggestedRanges: [string, string] | null;
  readonly licenseKey?: string;
  readonly loadingError: boolean;
}

class WebDataRocksStore
  extends Store<WebDataRocksStoreState>
  implements StoreMethods<WebDataRocksActionsInterface>
{
  constructor() {
    super();
    this.state = {
      // Visibility of overlay
      hidden: true,
      data: [],
      // Reference to component for api calls
      wdr: null,
      settingsLoaded: false,
      dataType: undefined,
      title: null,
      dataLoaded: false,
      suggestedRanges: null,
      licenseKey: undefined,
      loadingError: false,
    };
    this.bindActions(WebDataRocksActions);
  }

  private get wdr(): Pivot {
    const {wdr} = this.state;
    if (!wdr) {
      throw new Error("WebDataRocks isn't initialized!");
    }
    return wdr;
  }

  private get dataType(): WebDataType {
    const {dataType} = this.state;
    if (!dataType) {
      throw new Error('dataType is not stored');
    }
    return dataType;
  }

  private get allOptions() {
    const {conditions, formats, slice, options} = this.wdr.getReport() as Report;
    return {conditions, formats, options, slice, localization};
  }

  hideDialog() {
    this.setState({hidden: true, data: []});
  }

  loadSettings(dataType: WebDataType, pivot?: Pivot) {
    const wdr = pivot?.getReport ? pivot : this.wdr;

    const savedSettings = getSavedOptions();
    const typeSettings = savedSettings[dataType] || defaultOpts[dataType];
    savedSettings[dataType] = typeSettings;
    const report = wdr.getReport() as Report;
    try {
      wdr.setReport({...report, ...typeSettings});
      wdr.refresh();
    } catch (e) {
      console.error(e);
      console.error('Resetting to default config');

      savedSettings[dataType] = defaultOpts[dataType];
      localStorage.setItem(LocalStorageKey.WEB_DATA_ROCKS, JSON.stringify(savedSettings));
      wdr.setReport({...report, ...savedSettings});
      wdr.refresh();
    } finally {
      this.setState({settingsLoaded: true});
    }
  }

  saveSettings = (dataType: WebDataType) => {
    const savedSettings = getSavedOptions();
    savedSettings[dataType] = pickBy(this.allOptions);
    localStorage.setItem(LocalStorageKey.WEB_DATA_ROCKS, JSON.stringify(savedSettings));
  };

  resetToDefault() {
    const dataType = this.dataType;
    const savedSettings = getSavedOptions();
    savedSettings[dataType] = defaultOpts[dataType];
    localStorage.setItem(LocalStorageKey.WEB_DATA_ROCKS, JSON.stringify(savedSettings));
    this.loadSettings(dataType);
  }

  showDialog(title: string): void {
    this.setState({
      title,
      hidden: false,
      dataLoaded: false,
    });
  }

  fetchData([widgetConfig, managerId]: [WidgetConfig, string | undefined]): void {
    const dataType = dataTypeFromWidgetConfig(widgetConfig);
    const title =
      dataType === WebDataType.SW_REP
        ? 'Rep Scorecards'
        : dataType === WebDataType.SW_TEAM
        ? 'Team Scorecards'
        : '';
    this.setState({
      title,
      dataType,
      hidden: false,
      dataLoaded: false,
      suggestedRanges: null,
      loadingError: false,
    });
  }

  fetchDataError(error) {
    this.fetchDataTimeout(0);
    console.error(error);
  }

  fetchDataTimeout(timeout) {
    setTimeout(() => {
      if (!this.state.dataLoaded || !this.state.settingsLoaded) {
        console.error('WDR did not finish loading after 10 seconds', this.state);
        this.setState({
          loadingError: true,
        });
      }
    }, timeout);
  }

  fetchDataSuccess(results: any[]): void {
    this.setData([this.state.dataType!, results]);
  }

  setData([dataType, data]: [WebDataType, any[]]): void {
    // After 10 seconds of loading, force display to show errors if any
    this.fetchDataTimeout(10000);

    const {licenseKey} = this.state;

    // don't need a range check with flexmonster
    if (!licenseKey) {
      const range = validateSize(data, dataType);
      if (range) {
        // If we got a range back, that means our payloads too big.
        // tell the user to go smaller
        return this.setState({
          dataType,
          hidden: false,
          dataLoaded: true,
          settingsLoaded: true,
          suggestedRanges: range,
          data,
        });
      }
    }

    const config = optionsForType(dataType);
    const pivot = !!licenseKey ? Flexmonster : WebDataRocks;

    // Answer is not part of Coaching Plan export columns
    const mapping =
      dataType === WebDataType.COACHING_PLAN
        ? null
        : {
            Answer: {
              type: 'number',
            },
            Reason: {
              type: 'string',
            },
          };

    const onReportReady = () => {
      this.setState({
        dataLoaded: true,
        loadingError: false,
      });

      this.loadSettings(dataType, wdr);

      // Coaching Plan export has a dynamic schema generated on back-end and should
      //  use default settings to avoid clobbering other coaching plan exports schemas
      if (dataType === WebDataType.COACHING_PLAN) {
        this.resetToDefault();
      }

      // Format changes don't trigger ANY events
      wdr.on('reportchange', () => this.saveSettings(dataType));
    };

    const wdr = new pivot({
      licenseKey,
      container: '#wdr-root',
      toolbar: true,
      height: getWDRHeight(),
      width: '100%',
      report: {
        ...config,
        dataSource: {
          data,
          mapping: mapping,
        },
      },
      ready: onReportReady,
    });

    const noResults = data.length === 0;
    this.setState({
      wdr,
      dataType,
      // the complete hook won't get called if we give wdr an empty list
      // so don't set these flags in that case.
      settingsLoaded: noResults,
      dataLoaded: noResults,
      data,
    });
  }

  getLicenseKey() {}
  getLicenseKeyError(e: Error) {
    console.error(e);
  }
  getLicenseKeySuccess(licenseKey) {
    this.setState({licenseKey});
  }
}

/**
 * WebData does not rock, and it also can't accept more than 1MB of data.
 */
function validateSize(results: any[], dataType: WebDataType): [string, string] | undefined {
  if (results.length === 0) return;

  const dateColumn = dateColumnForDataType(dataType);
  const sortedResults = sortBy(results, dateColumn);

  const fromStart = getRange(sortedResults, dateColumn);
  const fromEnd = getRange(sortedResults.reverse(), dateColumn);

  if (fromStart && fromEnd) {
    // If we get a range back, then we went over. Return both
    return [fromStart, fromEnd];
  }
}

/**
 * Given a list of results, find the date we cross our threshold if any.
 * @param results data to check.
 * @param dateColumn column to check for the date.
 */
function getRange(results: any[], dateColumn: string): string | undefined {
  let days: OrderedSet<string> = OrderedSet();
  let totalSize = 0;
  for (const row of results) {
    totalSize = totalSize + byteSize(row) / 1000;

    //  YYYY-MM-DD 10 characters
    const date = row[dateColumn].substring(0, 10);

    // keep track of the dates for data we need the 2nd to last one
    days = days.add(date);

    // If we're approaching 1MB then return the last safe date
    if (totalSize > 950) {
      return moment(days.takeLast(2).first()).format('MM/DD/YYYY');
    }
  }
}

function dataTypeFromWidgetConfig(widgetConfig: WidgetConfig): WebDataType {
  const dataType =
    widgetConfig.widgetType === WidgetType.REP_SCORECARDS
      ? WebDataType.SW_REP
      : widgetConfig.widgetType === WidgetType.TEAM_SCORECARDS
      ? WebDataType.SW_TEAM
      : null;

  if (!dataType) {
    throw new Error(`Unsupported widgetType ${widgetConfig.widgetType}`);
  }
  return dataType;
}

export const getWDRHeight = () => {
  // If we're over cutoff then default to 700
  if (window.innerHeight > 870) {
    return 700;
  }

  // lower bound, we don't support resolutions below 600
  if (window.innerHeight < 600) {
    return 400;
  }

  // Scale based off the 375/600 proportion where we start hitting scrolls
  return (400 * window.innerHeight) / 600;
};

function getSavedOptions() {
  const val = localStorage.getItem(LocalStorageKey.WEB_DATA_ROCKS);
  if (!val) {
    // if we didn't find anything, go ahead and write the default to local storage
    localStorage.setItem(LocalStorageKey.WEB_DATA_ROCKS, JSON.stringify(defaultOpts));
    return defaultOpts;
  }
  return JSON.parse(val);
}

function optionsForType(dataType: WebDataType) {
  // fall back if defaults aren't saved for some type.
  return getSavedOptions()[dataType] || defaultOpts[dataType];
}

function dateColumnForDataType(dataType: WebDataType): string {
  if (dataType === WebDataType.SW_REP || dataType === WebDataType.SW_TEAM) {
    return 'Score Time';
  }

  if (dataType === WebDataType.NINE_BOX_METRICS || dataType === WebDataType.COACHING_PLAN) {
    return 'Date';
  }

  // Exists in data for both effectiveness and activity.
  return 'Start Date';
}

const strengthsAndWeaknessesSlice = {
  slice: {
    rows: [
      {
        uniqueName: 'Caller Team',
        sort: 'unsorted',
      },
      {
        uniqueName: 'Caller',
        sort: 'unsorted',
      },
      {
        uniqueName: 'Reviewer Team',
        sort: 'unsorted',
      },
      {
        uniqueName: 'Reviewer',
        sort: 'unsorted',
      },
    ],
    columns: [
      {
        uniqueName: 'Measures',
      },
      {
        uniqueName: 'Scorecard Name',
        sort: 'unsorted',
      },
      {
        uniqueName: 'Skill 1',
      },
      {
        uniqueName: 'Skill 2',
      },
      {
        uniqueName: 'Skill 3',
      },
      {
        uniqueName: 'Comment',
      },
      {
        uniqueName: 'Question',
      },
      {
        uniqueName: 'Raw Answer',
      },
      {
        uniqueName: 'Reason',
      },
      {
        uniqueName: 'Result Id',
      },
      {
        uniqueName: 'Call Id',
      },
      {
        uniqueName: 'Scheme',
      },
      {
        uniqueName: 'Score Time.Day',
      },
      {
        uniqueName: 'Score Time.Month',
      },
      {
        uniqueName: 'Score Time.Year',
      },
    ],
    measures: [
      {
        uniqueName: 'Answer',
        aggregation: 'average',
      },
    ],
    flatOrder: [
      'Caller Team',
      'Caller',
      'Answer',
      'Scorecard Name',
      'Comment',
      'Question',
      'Reason',
      'Skill 1',
      'Skill 2',
      'Skill 3',
      'Reviewer Team',
      'Reviewer',
      'Raw Answer',
      'Scheme',
      'Score Time.Day',
      'Score Time.Month',
      'Score Time.Year',
      'Result Id',
      'Call Id',
    ],
  },
};

const coachingActivitySlice = {
  slice: {
    rows: [],
    columns: [],
    measures: [],
    flatOrder: [
      'Email',
      'Start Date',
      'End Date',
      'Coaching Effectiveness Score',
      'Review Activity Hours',
      'Meeting Activity Hours',
      'Team Distribution',
      '% Of Requests Responded To',
      'Total Feedback',
      'Feedback Hours Listened',
      'Calls Scored',
      'Team Score % Change',
      'Team Score Rank',
      'Team Score Rank Change',
      'Team Activity % Change',
      'Team Activity Rank',
      'Team Activity Rank Change',
    ],
  },
};

const coachingEffectivenessSlice = {
  slice: {
    rows: [],
    columns: [],
    measures: [],
    flatOrder: [
      'Rep Email',
      'Start Date',
      'End Date',
      'Coach Email',
      'Hours of Coaching Total',
      'Hours of Coaching From Manager',
      'Rep Call Score % Change',
      'Rep Call Score Rank',
      'Rep Call Score Rank Change',
      'Rep Call Activity % Change',
      'Rep Call Activity Rank',
      'Rep Call Activity Rank Change',
    ],
  },
};

const nineBoxMetricsSlice = {
  slice: {
    rows: [],
    columns: [],
    measures: [],
    flatOrder: ['Email', 'Date', 'Metric', 'Value'],
  },
};

const coachingPlanSlice = {
  slice: {
    rows: [],
    columns: [],
    measures: [],
    flatOrder: [],
  },
};

const commonDefaults = {
  localization: localization,
  options: {
    grid: {
      type: 'flat',
      title: '',
      showFilter: true,
      showHeaders: true,
      showTotals: 'off',
      showGrandTotals: 'on',
      grandTotalsPosition: 'bottom',
      showExtraTotalLabels: false,
      showHierarchies: true,
      showHierarchyCaptions: true,
      drillthroughMaxRows: 1000,
      showReportFiltersArea: true,
      showAutoCalculationBar: true,
    },
    configuratorActive: false,
    configuratorButton: true,
    showAggregations: true,
    showCalculatedValuesButton: true,
    drillThrough: true,
    showDrillThroughConfigurator: true,
    sorting: 'on',
    datePattern: 'dd/MM/yyyy',
    dateTimePattern: 'dd/MM/yyyy HH:mm:ss',
    saveAllFormats: false,
    showDefaultSlice: true,
    showEmptyData: true,
    defaultHierarchySortName: 'asc',
    selectEmptyCells: true,
    showAggregationLabels: true,
  },
  formats: [
    {
      name: '',
      thousandsSeparator: ',',
      decimalSeparator: '.',
      decimalPlaces: 0,
      currencySymbol: '',
      currencySymbolAlign: 'left',
      nullValue: '',
      textAlign: 'right',
      isPercent: false,
    },
  ],
};

const defaultOpts = {
  [WebDataType.SW_TEAM]: {...commonDefaults, ...strengthsAndWeaknessesSlice},
  [WebDataType.SW_REP]: {...commonDefaults, ...strengthsAndWeaknessesSlice},
  [WebDataType.COACHING_ACTIVITY]: {...commonDefaults, ...coachingActivitySlice},
  [WebDataType.MANAGER_COACHING_ACTIVITY]: {...commonDefaults, ...coachingEffectivenessSlice},
  [WebDataType.NINE_BOX_METRICS]: {...commonDefaults, ...nineBoxMetricsSlice},
  [WebDataType.COACHING_PLAN]: {...commonDefaults, ...coachingPlanSlice},
  [WebDataType.ADMIN_USERS]: {...commonDefaults},
};

export default alt.createStore(WebDataRocksStore);
